import {
  useState,
  useCallback,
  useRef,
  type CSSProperties,
  type MouseEvent,
} from "react";
import { NodeViewWrapper, type NodeViewProps } from "@tiptap/react";
import { BlockEditModal } from "../BlockEditModal";
import {
  formatDocumentDateDisplay,
  formatLongDate,
  toDateInputValue,
  type DocumentDateMode,
} from "@/lib/document-date";

/**
 * Inline date chip in Flow paragraphs — click to switch Today vs fixed.
 * Always shows a resolved calendar date on the canvas.
 */
export function DateTokenNodeView({
  node,
  selected,
  updateAttributes,
  deleteNode,
}: NodeViewProps) {
  const mode = ((node.attrs.mode as DocumentDateMode) || "today") as DocumentDateMode;
  const value = (node.attrs.value as string) || "";
  const label = formatDocumentDateDisplay(
    mode === "today" ? "\\today" : value,
    mode,
  );

  const [open, setOpen] = useState(false);
  const [draftMode, setDraftMode] = useState<DocumentDateMode>(mode);
  const [draftIso, setDraftIso] = useState(toDateInputValue(value, mode));
  // After Save/Cancel/Close, the same pointer gesture can "click through" the
  // disappearing dialog onto this chip and re-open the modal. Suppress opens
  // briefly so only Delete (node gone) vs close (chip still there) differs.
  const suppressOpenUntil = useRef(0);

  const closeModal = useCallback(() => {
    suppressOpenUntil.current = Date.now() + 400;
    setOpen(false);
  }, []);

  const handleOpen = useCallback(
    (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (Date.now() < suppressOpenUntil.current) return;
      if (open) return;
      setDraftMode(mode);
      setDraftIso(toDateInputValue(value, mode));
      setOpen(true);
    },
    [mode, value, open],
  );

  // Stop ProseMirror from treating mousedown as "select atom only" so the
  // following click can open the modal on the first try. Do NOT stop clicks
  // globally via stopEvent — that breaks portaled dialog buttons.
  const stopSelectOnly = useCallback((e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleSave = useCallback(() => {
    updateAttributes({
      mode: draftMode,
      value: draftMode === "today" ? "" : draftIso,
    });
    closeModal();
  }, [draftMode, draftIso, updateAttributes, closeModal]);

  return (
    <NodeViewWrapper
      as="span"
      data-type="date-token"
      data-testid="date-token"
      className={selected ? "ProseMirror-selectednode" : undefined}
      style={{ display: "inline" }}
    >
      <button
        type="button"
        onMouseDown={stopSelectOnly}
        onClick={handleOpen}
        title={
          mode === "today"
            ? "Live date (\\today) — click to edit"
            : `Fixed date — click to edit`
        }
        style={{
          display: "inline",
          padding: "0 4px",
          margin: "0 1px",
          border: "none",
          borderRadius: 4,
          background: "var(--lilia-primary-subtle, rgba(10,118,164,0.1))",
          color: "var(--lilia-primary, #0A76A4)",
          font: "inherit",
          fontVariantNumeric: "tabular-nums",
          cursor: "pointer",
        }}
      >
        {label}
      </button>

      <BlockEditModal
        title="Edit inline date"
        open={open}
        onClose={closeModal}
        onSave={handleSave}
        onDelete={() => {
          closeModal();
          deleteNode();
        }}
        width={400}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <p style={{ margin: 0, fontSize: 12, color: "var(--lilia-text-muted)" }}>
            Body dates print in the paragraph. Document title-page date lives on
            the <strong>Title</strong> block (preamble <code>\date</code>), not here.
          </p>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              type="button"
              data-testid="inline-date-today"
              onClick={() => setDraftMode("today")}
              style={chip(draftMode === "today")}
            >
              Today
            </button>
            <button
              type="button"
              data-testid="inline-date-fixed"
              onClick={() => setDraftMode("fixed")}
              style={chip(draftMode === "fixed")}
            >
              Pick a date
            </button>
          </div>
          {draftMode === "today" ? (
            <p style={{ margin: 0, fontSize: 12 }}>
              Shows <strong>{formatLongDate()}</strong> · exports as{" "}
              <code>\today</code>
            </p>
          ) : (
            <input
              type="date"
              data-testid="inline-date-input"
              value={draftIso}
              onChange={(e) => setDraftIso(e.target.value)}
              style={{
                width: "100%",
                padding: "8px 12px",
                fontSize: 13,
                border: "1px solid var(--lilia-border)",
                borderRadius: 6,
              }}
            />
          )}
        </div>
      </BlockEditModal>
    </NodeViewWrapper>
  );
}

function chip(active: boolean): CSSProperties {
  return {
    flex: 1,
    padding: "8px 10px",
    borderRadius: 8,
    border: active
      ? "1.5px solid var(--lilia-primary, #0A76A4)"
      : "1px solid var(--lilia-border, #ddd)",
    background: active
      ? "var(--lilia-primary-subtle, rgba(10,118,164,0.08))"
      : "var(--lilia-surface, #fff)",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
  };
}
